import { Badge } from "../ui/Badge";
import type { TaskRunBriefEvidence } from "../../../../shared/types";

interface RecommendationEvidenceDrawerProps {
  findings: TaskRunBriefEvidence[];
  title?: string;
  compact?: boolean;
}

export function RecommendationEvidenceDrawer({
  findings,
  title = "Supporting findings",
  compact = false,
}: RecommendationEvidenceDrawerProps) {
  if (findings.length === 0) return null;

  return (
    <details className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <summary className="cursor-pointer list-none text-sm font-medium text-slate-800">
        {title} ({findings.length})
      </summary>
      <div className="mt-3 space-y-3">
        {findings.map((finding) => {
          const sourceUrl = typeof finding.source_url === "string" ? finding.source_url.trim() : "";
          const hasExternalSource =
            /^https?:\/\//i.test(sourceUrl)
            && finding.source_access !== "internal"
            && finding.source_access !== "unavailable";
          const unavailableLabel = finding.source_label ?? finding.domain ?? finding.query ?? null;
          const accessLabel =
            finding.source_access === "internal"
              ? "Internal source"
              : finding.source_access === "unavailable"
              ? unavailableLabel
                ? `Source unavailable (${unavailableLabel})`
                : "Source unavailable"
              : finding.source_label ?? finding.domain ?? finding.query ?? null;
          return (
            <div key={finding.finding_id} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <Badge variant="info">{finding.type}</Badge>
              <span>{finding.score}/100</span>
              {finding.query && <span>Query: {finding.query}</span>}
              {finding.domain && <span>Domain: {finding.domain}</span>}
              {!compact && finding.published_at && (
                <span>Published: {new Date(finding.published_at).toLocaleDateString()}</span>
              )}
            </div>
            <p className="mt-2 text-sm font-medium text-slate-900">{finding.title}</p>
            {hasExternalSource && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex text-xs text-blue-600 hover:underline"
              >
                Open source
              </a>
            )}
            {!hasExternalSource && accessLabel && (
              <p className="mt-1 text-xs text-slate-500">{accessLabel}</p>
            )}
            </div>
          );
        })}
      </div>
    </details>
  );
}
